import React, { useState, useEffect, JSX } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import { getOrganizationHeaders } from '../orgContext';

interface Category {
  id: number;
  name: string;
  description: string;
  sortOrder?: number;
  includeInTasks?: boolean;
  detailedDescription?: string;
  tips?: string;
  example?: string;
  parentCategoryId?: number | null;
  accessibilityType?: number; // 1=Free, 2=Bronze, 3=Silver, 4=Gold
  isEnabled?: boolean; // Whether the category is enabled/active
  courseIds?: number[];
  subCategories?: Category[];
}

interface CourseOption {
  id: number;
  name: string;
}

const ManageCategories: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [categories, setCategories] = useState<Category[]>([]);
  const [flatCategories, setFlatCategories] = useState<Category[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set());
  const [loadingChildrenIds, setLoadingChildrenIds] = useState<Set<number>>(new Set());
  const [loadedChildrenIds, setLoadedChildrenIds] = useState<Set<number>>(new Set());
  const [leafCategoryIds, setLeafCategoryIds] = useState<Set<number>>(new Set());
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [detailedDescriptionTab, setDetailedDescriptionTab] = useState<'edit' | 'preview'>('edit');
  const [tipsTab, setTipsTab] = useState<'edit' | 'preview'>('edit');
  const [exampleTab, setExampleTab] = useState<'edit' | 'preview'>('edit');

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;

  const [formData, setFormData] = useState<Category>({
    id: 0,
    name: '',
    description: '',
    sortOrder: 1,
    detailedDescription: '',
    tips: '',
    example: '',
    includeInTasks: true,
    parentCategoryId: null,
    accessibilityType: 1,
    isEnabled: true,
    courseIds: [],
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchRootCategories();
    fetchFlatCategories();
    fetchCourses();
  }, []);

  const getManagementToken = async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  };

  useEffect(() => {
    if (flatCategories.length > 0 && !editingId && !isAdding) {
      const firstCategory = [...flatCategories]
        .sort((first, second) => {
          const firstOrder = first.sortOrder && first.sortOrder > 0 ? first.sortOrder : 1;
          const secondOrder = second.sortOrder && second.sortOrder > 0 ? second.sortOrder : 1;
          if (firstOrder !== secondOrder) {
            return firstOrder - secondOrder;
          }
          return first.name.localeCompare(second.name);
        })[0];

      if (firstCategory) {
        setFormData(firstCategory);
        setEditingId(firstCategory.id);
      }
    }
  }, [flatCategories, editingId, isAdding]);

  const fetchRootCategories = async () => {
    try {
      setLoading(true);
      const apiUrl = `${config.apiBaseUrl}/api/categories?includeHierarchy=false`;
      console.log('🔍 Fetching from:', apiUrl);

      const token = await getManagementToken();
      
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`API Error ${response.status}: ${errorData}`);
      }
      const data = await response.json();
      console.log('✅ Loaded root categories:', data.length, 'items');
      setCategories(Array.isArray(data) ? data : []);
      setExpandedCategoryIds(new Set());
      setLoadingChildrenIds(new Set());
      setLoadedChildrenIds(new Set());
      setLeafCategoryIds(new Set());
      setError(null);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load categories';
      setError(errorMessage);
      console.error('❌ Error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFlatCategories = async () => {
    try {
      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/categories/flat`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load category list');
      }

      const data = await response.json();
      setFlatCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load flat categories:', err);
    }
  };

  const fetchCourses = async () => {
    try {
      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/courses`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load courses');
      }

      const data = await response.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load courses:', err);
    }
  };

  const sortCategories = (cats: Category[]): Category[] => {
    return [...cats].sort((first, second) => {
      const firstOrder = first.sortOrder && first.sortOrder > 0 ? first.sortOrder : 1;
      const secondOrder = second.sortOrder && second.sortOrder > 0 ? second.sortOrder : 1;
      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }
      return first.name.localeCompare(second.name);
    });
  };

  const replaceChildren = (cats: Category[], parentId: number, children: Category[]): Category[] => {
    return cats.map((cat) => {
      if (cat.id === parentId) {
        return {
          ...cat,
          subCategories: children,
        };
      }

      if (cat.subCategories && cat.subCategories.length > 0) {
        return {
          ...cat,
          subCategories: replaceChildren(cat.subCategories, parentId, children),
        };
      }

      return cat;
    });
  };

  const fetchChildrenForCategory = async (categoryId: number) => {
    setLoadingChildrenIds((previous) => {
      const next = new Set(previous);
      next.add(categoryId);
      return next;
    });

    try {
      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/categories/${categoryId}/subcategories?includeHierarchy=false`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load subcategories');
      }

      const children = await response.json();
      const normalizedChildren: Category[] = Array.isArray(children) ? children : [];

      setCategories((previous) => replaceChildren(previous, categoryId, normalizedChildren));
      setLoadedChildrenIds((previous) => {
        const next = new Set(previous);
        next.add(categoryId);
        return next;
      });

      if (normalizedChildren.length === 0) {
        setLeafCategoryIds((previous) => {
          const next = new Set(previous);
          next.add(categoryId);
          return next;
        });
      } else {
        setLeafCategoryIds((previous) => {
          const next = new Set(previous);
          next.delete(categoryId);
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to load subcategories:', err);
      setError('Failed to load subcategories');
    } finally {
      setLoadingChildrenIds((previous) => {
        const next = new Set(previous);
        next.delete(categoryId);
        return next;
      });
    }
  };

  const toggleCategoryExpand = async (categoryId: number) => {
    if (expandedCategoryIds.has(categoryId)) {
      setExpandedCategoryIds((previous) => {
        const next = new Set(previous);
        next.delete(categoryId);
        return next;
      });
      return;
    }

    if (!loadedChildrenIds.has(categoryId) && !loadingChildrenIds.has(categoryId)) {
      await fetchChildrenForCategory(categoryId);
    }

    setExpandedCategoryIds((previous) => {
      const next = new Set(previous);
      next.add(categoryId);
      return next;
    });
  };

  const handleEdit = (category: Category) => {
    setFormData({
      ...category,
      sortOrder: category.sortOrder && category.sortOrder > 0 ? category.sortOrder : 1,
      isEnabled: category.isEnabled !== false,
      courseIds: category.courseIds || [],
    });
    setEditingId(category.id);
    setIsAdding(false);
  };

  const handleAddNew = () => {
    setFormData({
      id: 0,
      name: '',
      description: '',
      sortOrder: 1,
      detailedDescription: '',
      tips: '',
      example: '',
      includeInTasks: true,
      parentCategoryId: null,
      accessibilityType: 1,
      isEnabled: true,
      courseIds: [],
    });
    setEditingId(null);
    setIsAdding(true);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({
      id: 0,
      name: '',
      description: '',
      sortOrder: 1,
      detailedDescription: '',
      tips: '',
      example: '',
      includeInTasks: true,
      parentCategoryId: null,
      accessibilityType: 1,
      isEnabled: true,
      courseIds: [],
    });
  };

  const handleCourseToggle = (courseId: number) => {
    setFormData(prev => ({
      ...prev,
      courseIds: prev.courseIds?.includes(courseId)
        ? prev.courseIds.filter(id => id !== courseId)
        : [...(prev.courseIds || []), courseId],
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      let response;
      const token = await getManagementToken();
      const commonHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...getOrganizationHeaders(),
      };
      if (isAdding) {
        response = await fetch(`${config.apiBaseUrl}/api/categories`, {
          method: 'POST',
          headers: commonHeaders,
          body: JSON.stringify(formData),
        });
      } else {
        response = await fetch(`${config.apiBaseUrl}/api/categories/${formData.id}`, {
          method: 'PUT',
          headers: commonHeaders,
          body: JSON.stringify(formData),
        });
      }

      if (!response.ok) throw new Error('Failed to save category');
      
      if (isAdding) {
        // For new categories, refresh to get proper hierarchy
        await Promise.all([fetchRootCategories(), fetchFlatCategories()]);
        handleCancel();
      } else {
        // Find the original category to check if parent changed
        const findOriginal = (cats: Category[]): Category | null => {
          for (const cat of cats) {
            if (cat.id === formData.id) return cat;
            if (cat.subCategories) {
              const found = findOriginal(cat.subCategories);
              if (found) return found;
            }
          }
          return null;
        };
        
        const originalCategory = findOriginal(categories);
        const parentChanged = originalCategory?.parentCategoryId !== formData.parentCategoryId;
        
        if (parentChanged) {
          // Refresh if parent category changed (affects hierarchy)
          await Promise.all([fetchRootCategories(), fetchFlatCategories()]);
        } else {
          // Just update in place without refresh
          const updateCategories = (cats: Category[]): Category[] => {
            return cats.map(cat => 
              cat.id === formData.id ? formData : cat
            ).map(cat => ({
              ...cat,
              subCategories: cat.subCategories ? updateCategories(cat.subCategories) : []
            }));
          };
          setCategories((previous) => updateCategories(previous));
          setFlatCategories((previous) => previous.map((cat) => (cat.id === formData.id ? formData : cat)));
          setFormData(formData); // Keep the form open with updated data
        }
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to save category');
      console.error(err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = await getManagementToken();
      const response = await fetch(`${config.apiBaseUrl}/api/categories/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          ...getOrganizationHeaders(),
        },
      });

      if (!response.ok) throw new Error('Failed to delete category');
      
      await Promise.all([fetchRootCategories(), fetchFlatCategories()]);
      setError(null);
    } catch (err) {
      setError('Failed to delete category');
      console.error(err);
    }
  };

  const getAllCategories = (cats: Category[] = flatCategories): Category[] => {
    let all: Category[] = [];
    const sortedCategories = sortCategories(cats);

    sortedCategories.forEach(cat => {
      all.push(cat);
      if (cat.subCategories && cat.subCategories.length > 0) {
        all = all.concat(getAllCategories(cat.subCategories));
      }
    });
    return all;
  };

  const getLevelColors = (level: number) => {
    const colors = [
      { bg: '#e3f2fd', border: '#1976d2' }, // Blue for level 0
      { bg: '#e8f5e9', border: '#388e3c' }, // Green for level 1
      { bg: '#fffde7', border: '#f57f17' }, // Yellow for level 2
      { bg: '#fce4ec', border: '#c2185b' }, // Pink for level 3
      { bg: '#f3e5f5', border: '#7b1fa2' }, // Purple for level 4
      { bg: '#e0f2f1', border: '#00796b' }, // Teal for level 5
    ];
    return colors[level % colors.length];
  };

  const renderCategoryTree = (cats: Category[], level: number = 0): JSX.Element[] => {
    const sortedCategories = sortCategories(cats);

    return sortedCategories.map(cat => {
      const levelColors = getLevelColors(level);
      const isEditing = editingId === cat.id;
      const bgColor = isEditing ? '#e3f2fd' : levelColors.bg;
      const borderColor = isEditing ? '#2196f3' : levelColors.border;

      return (
        <div key={cat.id}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: isMobile ? 8 : 10,
              backgroundColor: bgColor,
              border: `2px solid ${borderColor}`,
              borderRadius: 6,
              marginBottom: 6,
              marginLeft: `${level * 20}px`,
              flexWrap: 'wrap',
            }}
          >
          {!leafCategoryIds.has(cat.id) && (
            <button
              type="button"
              onClick={() => void toggleCategoryExpand(cat.id)}
              disabled={loadingChildrenIds.has(cat.id)}
              style={{
                width: 24,
                height: 24,
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                backgroundColor: '#fff',
                color: '#334155',
                cursor: loadingChildrenIds.has(cat.id) ? 'wait' : 'pointer',
                fontSize: 12,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              title={expandedCategoryIds.has(cat.id) ? 'Collapse subcategories' : 'Expand subcategories'}
            >
              {loadingChildrenIds.has(cat.id) ? '…' : expandedCategoryIds.has(cat.id) ? '−' : '+'}
            </button>
          )}
          <div style={{ flex: 1, minWidth: isMobile ? '100%' : '180px' }}>
            <p style={{ fontSize: isMobile ? 13 : 14, fontWeight: 500, margin: 0, color: '#222' }}>
              {cat.name}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={() => handleEdit(cat)}
              style={{
                padding: isMobile ? '5px 10px' : '6px 12px',
                backgroundColor: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: isMobile ? 11 : 12,
                fontWeight: 600,
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => handleDelete(cat.id)}
              style={{
                padding: isMobile ? '5px 10px' : '6px 12px',
                backgroundColor: '#f44336',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: isMobile ? 11 : 12,
                fontWeight: 600,
              }}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
        {expandedCategoryIds.has(cat.id) && cat.subCategories && cat.subCategories.length > 0 && renderCategoryTree(cat.subCategories, level + 1)}
      </div>
    );
    });
  };

  return (
    <div style={{
      maxWidth: 1400,
      margin: '0 auto',
      padding: isMobile ? '16px 8px' : isTablet ? '20px 12px' : '24px 20px',
      textAlign: 'left',
    }}>
      <div style={{
        marginBottom: isMobile ? 16 : 24,
        padding: isMobile ? '14px 16px' : '16px 20px',
        borderRadius: 12,
        background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 700,
            margin: 0,
            textAlign: 'left',
          }}>
            Manage Categories
          </h1>
          <p style={{
            margin: '6px 0 0 0',
            fontSize: isMobile ? 12 : 14,
            opacity: 0.92,
          }}>
            Organize topic hierarchy and category-to-course mappings for the current organization.
          </p>
        </div>
        <div style={{
          fontSize: isMobile ? 12 : 13,
          fontWeight: 600,
          background: 'rgba(255,255,255,0.18)',
          padding: '6px 10px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
        }}>
          {flatCategories.length} total categories
        </div>
      </div>

      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: isMobile ? 12 : 16,
          borderRadius: 8,
          marginBottom: 16,
          fontSize: isMobile ? '0.9rem' : '1rem',
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: 24,
        flexDirection: isMobile || isTablet ? 'column' : 'row',
      }}>
        {/* Left side - Categories List */}
        <div style={{
          flex: (editingId !== null || isAdding) && !isMobile && !isTablet ? '0 0 46%' : 1,
          maxWidth: (editingId !== null || isAdding) && !isMobile && !isTablet ? '520px' : 'none',
          minWidth: 0,
        }}>
          <button
            onClick={handleAddNew}
            style={{
              marginBottom: 24,
              padding: isMobile ? '10px 20px' : '12px 24px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
            }}
          >
            ➕ Add New Category
          </button>

          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? 24 : 48,
              color: '#666',
              fontSize: isMobile ? 14 : 16,
            }}>
              Loading categories...
            </div>
          ) : categories.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? 24 : 48,
              color: '#999',
              fontSize: isMobile ? 14 : 16,
            }}>
              No categories available
            </div>
          ) : (
            <div style={{
              marginLeft: isMobile ? 0 : 16,
            }}>
              <h2 style={{
                fontSize: isMobile ? 16 : 18,
                fontWeight: 600,
                marginBottom: 16,
                color: '#222',
              }}>
                {categories.length} Categories
              </h2>
              {renderCategoryTree(categories)}
            </div>
          )}
        </div>

        {/* Right side - Edit Form */}
        {(editingId !== null || isAdding) && (
          <div style={{
            flex: (isMobile || isTablet) ? 1 : '1 1 auto',
            minWidth: isMobile || isTablet ? '100%' : '350px',
            maxWidth: 'none',
            backgroundColor: '#f5f5f5',
            border: '2px solid #2196f3',
            borderRadius: 8,
            padding: isMobile ? 16 : 24,
            height: 'fit-content',
            position: isMobile || isTablet ? 'relative' : 'sticky',
            top: 20,
          }}>
            <h2 style={{
              fontSize: isMobile ? 18 : 20,
              fontWeight: 600,
              marginBottom: 16,
              color: '#222',
            }}>
              {isAdding ? 'Add New Category' : `Edit Category: ${formData.name}`}
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 16,
              marginBottom: 16,
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter category name"
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Short Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter short description"
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Order Number
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.sortOrder || 1}
                  onChange={(e) => setFormData({ ...formData, sortOrder: Math.max(1, parseInt(e.target.value || '1', 10)) })}
                  placeholder="1"
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Parent Category
                </label>
                <select
                  value={formData.parentCategoryId || ''}
                  onChange={(e) => setFormData({ ...formData, parentCategoryId: e.target.value ? parseInt(e.target.value) : null })}
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">-- None (Top Level) --</option>
                  {getAllCategories()
                    .filter(cat => cat.id !== formData.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Accessibility Type
                </label>
                <select
                  value={formData.accessibilityType || 1}
                  onChange={(e) => setFormData({ ...formData, accessibilityType: parseInt(e.target.value) })}
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <option value={1}>1 - Free</option>
                  <option value={2}>2 - Bronze</option>
                  <option value={3}>3 - Silver</option>
                  <option value={4}>4 - Gold</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Include In Questions
                </label>
                <select
                  value={formData.includeInTasks === false ? 'false' : 'true'}
                  onChange={(e) => setFormData({ ...formData, includeInTasks: e.target.value === 'true' })}
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <option value="true">Yes - Show in Questions</option>
                  <option value="false">No - Exclude from Questions</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Category Status
                </label>
                <select
                  value={formData.isEnabled === false ? 'false' : 'true'}
                  onChange={(e) => setFormData({ ...formData, isEnabled: e.target.value === 'true' })}
                  style={{
                    width: '100%',
                    padding: isMobile ? 10 : 12,
                    fontSize: isMobile ? 13 : 14,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: '#222',
                }}>
                  Assigned Courses ({formData.courseIds?.length || 0})
                </label>
                <div style={{
                  maxHeight: isMobile ? 160 : 200,
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 4,
                  backgroundColor: '#fff',
                  padding: 8,
                }}>
                  {courses.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#777' }}>No courses available</p>
                  ) : (
                    courses.map(course => (
                      <label
                        key={course.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 4px',
                          cursor: 'pointer',
                          fontSize: isMobile ? 12 : 13,
                          color: '#222',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.courseIds?.includes(course.id) || false}
                          onChange={() => handleCourseToggle(course.id)}
                        />
                        <span>{course.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#666' }}>
                  Saving this category will also update its course mappings.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                fontSize: isMobile ? 12 : 13,
                fontWeight: 600,
                marginBottom: 6,
                color: '#222',
              }}>
                Detailed Description (HTML Supported)
              </label>
              <div style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f9f9f9',
                }}>
                  <button
                    onClick={() => setDetailedDescriptionTab('edit')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: detailedDescriptionTab === 'edit' ? '#fff' : '#f9f9f9',
                      borderBottom: detailedDescriptionTab === 'edit' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: detailedDescriptionTab === 'edit' ? '#2196f3' : '#666',
                    }}
                  >
                    📝 Edit
                  </button>
                  <button
                    onClick={() => setDetailedDescriptionTab('preview')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: detailedDescriptionTab === 'preview' ? '#fff' : '#f9f9f9',
                      borderBottom: detailedDescriptionTab === 'preview' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: detailedDescriptionTab === 'preview' ? '#2196f3' : '#666',
                    }}
                  >
                    👁️ Preview
                  </button>
                </div>
                
                {detailedDescriptionTab === 'edit' && (
                  <textarea
                    value={formData.detailedDescription || ''}
                    onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value })}
                    placeholder="Enter HTML content (e.g., <h1>Title</h1>, <p>Paragraph</p>, <br/>, <strong>Bold</strong>, etc.)"
                    style={{
                      width: '100%',
                      minHeight: isMobile ? 120 : 150,
                      padding: isMobile ? 10 : 12,
                      fontSize: isMobile ? 12 : 13,
                      border: 'none',
                      borderRadius: 0,
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                )}
                
                {detailedDescriptionTab === 'preview' && (
                  <div style={{
                    minHeight: isMobile ? 120 : 150,
                    padding: isMobile ? 10 : 12,
                    backgroundColor: '#fff',
                    overflow: 'auto',
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: formData.detailedDescription || '<p style="color: #999;">No content to preview</p>' }} />
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                fontSize: isMobile ? 12 : 13,
                fontWeight: 600,
                marginBottom: 6,
                color: '#222',
              }}>
                Tips (HTML Supported)
              </label>
              <div style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f9f9f9',
                }}>
                  <button
                    onClick={() => setTipsTab('edit')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: tipsTab === 'edit' ? '#fff' : '#f9f9f9',
                      borderBottom: tipsTab === 'edit' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: tipsTab === 'edit' ? '#2196f3' : '#666',
                    }}
                  >
                    📝 Edit
                  </button>
                  <button
                    onClick={() => setTipsTab('preview')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: tipsTab === 'preview' ? '#fff' : '#f9f9f9',
                      borderBottom: tipsTab === 'preview' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: tipsTab === 'preview' ? '#2196f3' : '#666',
                    }}
                  >
                    👁️ Preview
                  </button>
                </div>
                
                {tipsTab === 'edit' && (
                  <textarea
                    value={formData.tips || ''}
                    onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
                    placeholder="Enter HTML content (e.g., <h1>Title</h1>, <p>Paragraph</p>, <br/>, <strong>Bold</strong>, etc.)"
                    style={{
                      width: '100%',
                      minHeight: isMobile ? 80 : 100,
                      padding: isMobile ? 10 : 12,
                      fontSize: isMobile ? 12 : 13,
                      border: 'none',
                      borderRadius: 0,
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                )}
                
                {tipsTab === 'preview' && (
                  <div style={{
                    minHeight: isMobile ? 80 : 100,
                    padding: isMobile ? 10 : 12,
                    backgroundColor: '#fff',
                    overflow: 'auto',
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: formData.tips || '<p style="color: #999;">No content to preview</p>' }} />
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                fontSize: isMobile ? 12 : 13,
                fontWeight: 600,
                marginBottom: 6,
                color: '#222',
              }}>
                Example (HTML Supported)
              </label>
              <div style={{
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: '#f9f9f9',
                }}>
                  <button
                    onClick={() => setExampleTab('edit')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: exampleTab === 'edit' ? '#fff' : '#f9f9f9',
                      borderBottom: exampleTab === 'edit' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: exampleTab === 'edit' ? '#2196f3' : '#666',
                    }}
                  >
                    📝 Edit
                  </button>
                  <button
                    onClick={() => setExampleTab('preview')}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      backgroundColor: exampleTab === 'preview' ? '#fff' : '#f9f9f9',
                      borderBottom: exampleTab === 'preview' ? '2px solid #2196f3' : 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                      color: exampleTab === 'preview' ? '#2196f3' : '#666',
                    }}
                  >
                    👁️ Preview
                  </button>
                </div>
                
                {exampleTab === 'edit' && (
                  <textarea
                    value={formData.example || ''}
                    onChange={(e) => setFormData({ ...formData, example: e.target.value })}
                    placeholder="Enter HTML content (e.g., <h1>Title</h1>, <p>Paragraph</p>, <br/>, <strong>Bold</strong>, etc.)"
                    style={{
                      width: '100%',
                      minHeight: isMobile ? 80 : 100,
                      padding: isMobile ? 10 : 12,
                      fontSize: isMobile ? 12 : 13,
                      border: 'none',
                      borderRadius: 0,
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                    }}
                  />
                )}
                
                {exampleTab === 'preview' && (
                  <div style={{
                    minHeight: isMobile ? 80 : 100,
                    padding: isMobile ? 10 : 12,
                    backgroundColor: '#fff',
                    overflow: 'auto',
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: formData.example || '<p style="color: #999;">No content to preview</p>' }} />
                  </div>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                }}
              >
                ✕ Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 600,
                }}
              >
                ✓ Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageCategories;
